import { IVehicleSearchFormatResults } from 'core/vehicles/mappings/vehicle'
import { groupBy } from '@cbgms/base/utils/helpers'
import maxBy from 'lodash/maxBy'
import { ErrorWithErrData, ISearchResponse } from '@cbgms/api'
import { ResultObjectType } from '@cbgms/api/modules/enums'

export const vehicleSearchFormat = (search: {
  Results: ISearchResponse['Results']
  Errors?: ISearchResponse['Errors']
}): IVehicleSearchFormatResults[] => {
  if (!search) return []

  const normalSearchResults = search.Results.filter(r => r.Type !== 'generic_vehicle' && r.Type !== 'database_vehicle')
  const vehicleSearchResults = search.Results.filter(r => r.Type === 'generic_vehicle')
  const dbVehicleSearchResults = search.Results.filter(r => r.Type === 'database_vehicle')

  // regroup vehiclesearchresults by registration (11-xx-11, with multiple hbase types)
  const vehicleGroups = groupBy(vehicleSearchResults, 'Vehicle.Registration')
  const dbVehicleGroups = groupBy(dbVehicleSearchResults, 'Vehicle.Registration')

  const regroupedVehicles = vehicleGroups.map(groups => {
    return {
      Score: maxBy(groups.Values, 'Score')!.Score, // select the best score
      Type: groups.Values[0].Type, // these are all vehicles
      Vehicle: { Key: groups.Key, Values: groups.Values.map((v: any) => v.Vehicle) } // Pass group as result
    }
  })

  const dbRegroupedVehicles = dbVehicleGroups.map(groups => {
    return {
      Score: maxBy(groups.Values, 'Score')!.Score, // select the best score
      Type: groups.Values[0].Type, // these are all vehicles
      Vehicle: { Key: groups.Key, Values: groups.Values.map((v: any) => v.Vehicle) } // Pass group as result
    }
  })

  let errors: IVehicleSearchFormatResults[] = []

  if (search.Errors?.length) {
    errors = [
      // add a single error element
      {
        Score: 1, // low score, so it ends up on the bottom
        Type: ResultObjectType.Error,
        Errors: search.Errors?.map(e => e.Err)
      }
    ]
  }

  return [...normalSearchResults, ...dbRegroupedVehicles, ...regroupedVehicles, ...errors].sort(
    (a, b) => b.Score - a.Score
  ) as IVehicleSearchFormatResults[]
}

export const groupVehicleResults = (data: ISearchResponse, resultType: ResultObjectType, groupByKey: string) => {
  const typeResults = (data.Results || []).filter(result => result.Type === resultType)
  const groupedResults = groupBy(typeResults, groupByKey)
  return groupedResults.map(group => ({
    Score: maxBy(group.Values, 'Score')?.Score || 0, // select the best score
    Type: group.Values[0].Type,
    Vehicle: { Key: group.Key, Values: group.Values.map(v => v.Vehicle!) } // Pass group as result
  }))
}

export const flattenTyreSizes = (data: ISearchResponse): IVehicleSearchFormatResults[] => {
  const tyreResults = (data.Results || []).filter(result => result.Type === ResultObjectType.TyreSize)
  const result = tyreResults.flatMap(tyreResult =>
    tyreResult.TyreSize?.map(t => ({
      ...tyreResult, // spread parent
      TyreSize: t // flatten array, TireSize becomes a single item
    }))
  )
  const flatResults = result.filter(a => !!a) as IVehicleSearchFormatResults[]
  return flatResults
}

const emptySearchResponse: ISearchResponse = {
  Errors: [],
  Results: [],
  Filters: [],
  TotalResultCount: 0,
  LimitedResultCount: 0
}

/**
 * formatSearchResults will create entries as search results
 */
export const formatSearchResults = (data: ISearchResponse | undefined): IVehicleSearchFormatResults[] => {
  if (!data) {
    data = emptySearchResponse
  }
  // we need to do some fancy tricks with vehicle results if multiple are returned (ie multiple hbase)
  const resultsWithoutCustomFormatting = (data.Results || []).filter(
    result =>
      result.Type !== ResultObjectType.DatabaseVehicle &&
      result.Type !== ResultObjectType.GenericVehicle &&
      result.Type !== ResultObjectType.VehicleWithAccount &&
      result.Type !== ResultObjectType.TyreSize // tyres sizes will be flattened
  ) as Omit<IVehicleSearchFormatResults, 'Vehicle'>[]

  return [
    ...resultsWithoutCustomFormatting,
    ...groupVehicleResults(data, ResultObjectType.DatabaseVehicle, 'Vehicle.Registration'),
    ...groupVehicleResults(data, ResultObjectType.GenericVehicle, 'Vehicle.Vin'),
    ...groupVehicleResults(data, ResultObjectType.VehicleWithAccount, 'Vehicle.Registration'),
    ...flattenTyreSizes(data)
  ].sort((a, b) => b.Score - a.Score)
}

/**
 * formatSearchErrors will create a single error entrie as search results
 */
export const formatSearchErrors = (
  data: ISearchResponse | undefined,
  filter?: (data: ISearchResponse | undefined) => ErrorWithErrData[] | undefined
): IVehicleSearchFormatResults[] => {
  if (!data) {
    return []
  }
  const errors = (filter && filter(data)) || data.Errors
  return errors?.length
    ? [
        {
          Score: -10, // low score, so it ends up on the bottom
          Type: ResultObjectType.Error,
          Errors: errors?.map(e => e.Err)
        }
      ]
    : []
}
